
import { useCallback, useRef } from 'react';
import { 
  loadGameProgressFromSupabase, 
  saveGameProgressToSupabase 
} from '@/lib/supabase/game';
import type { GameData, SyncStatus } from './types';
import { DEFAULT_GAME_DATA } from './constants';

export const useGameDataSync = (
  userData: any,
  loadFromLocalStorage: () => GameData | null,
  saveToLocalStorage: (data: GameData) => void,
  hasGameProgress: (data: GameData) => boolean
) => {
  // Use refs to track sync state and prevent infinite loops
  const isSyncingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const lastUserEmailRef = useRef<string | null>(null);

  /**
   * Load game data from Supabase and localStorage - simplified version
   */
  const loadGameData = useCallback(async (
    setGameData: (data: GameData) => void,
    setLastSyncTime: (time: number) => void,
    setSyncStatus: (status: SyncStatus) => void,
    setIsLoading: (loading: boolean) => void
  ) => {
    const userEmail = userData?.email;
    
    // Guard against multiple loads and missing user
    if (!userEmail || isSyncingRef.current) {
      console.log('🎮 Game Data - Skip load: no email or already loading');
      setIsLoading(false);
      setSyncStatus('never');
      return;
    }

    // Check if user email changed - force fresh sync for new users
    const isNewUser = lastUserEmailRef.current !== userEmail;
    if (hasInitializedRef.current && !isNewUser) {
      console.log('🎮 Game Data - Already initialized for same user');
      setIsLoading(false);
      return;
    }

    console.log('🎮 Game Data - Starting load for user:', userEmail);
    setIsLoading(true);
    setSyncStatus('loading');
    isSyncingRef.current = true;
    
    try {
      // Try Supabase first
      const supabaseData = await loadGameProgressFromSupabase(userEmail, userData);
      
      if (supabaseData) {
        console.log('🎮 Game Data - Found Supabase data:', supabaseData);
        setGameData(supabaseData);
        saveToLocalStorage(supabaseData);
        setLastSyncTime(Date.now());
        setSyncStatus(hasGameProgress(supabaseData) ? 'with_data' : 'empty');
      } else {
        // Try localStorage fallback
        const localData = loadFromLocalStorage();
        
        if (localData && hasGameProgress(localData)) {
          console.log('🎮 Game Data - Using localStorage data:', localData);
          setGameData(localData);
          setSyncStatus('with_data');
          
          // Try to sync to Supabase (non-blocking)
          saveGameProgressToSupabase(userEmail, localData, userData)
            .then(success => {
              if (success) {
                setLastSyncTime(Date.now());
                console.log('🎮 Game Data - Synced localStorage to Supabase');
              }
            })
            .catch(error => {
              console.error('🎮 Game Data - Error syncing to Supabase:', error);
              setSyncStatus('error');
            });
        } else {
          // Try legacy recovery
          const { recoverLegacyGameData } = await import('@/lib/game-data-recovery');
          const recoveredData = recoverLegacyGameData();
          
          if (recoveredData && hasGameProgress(recoveredData)) {
            console.log('🎮 Game Data - Recovered legacy data:', recoveredData);
            setGameData(recoveredData);
            saveToLocalStorage(recoveredData);
            setSyncStatus('with_data');
            
            // Sync to Supabase (non-blocking)
            saveGameProgressToSupabase(userEmail, recoveredData, userData)
              .then(success => {
                if (success) {
                  setLastSyncTime(Date.now());
                  console.log('🎮 Game Data - Synced recovered data to Supabase');
                }
              })
              .catch(error => {
                console.error('🎮 Game Data - Error syncing recovered data:', error);
              });
          } else {
            // No data found, create initial empty record
            console.log('🎮 Game Data - No data found, creating initial record');
            setSyncStatus('empty');
            
            // Create initial Supabase record (non-blocking)
            saveGameProgressToSupabase(userEmail, DEFAULT_GAME_DATA, userData)
              .then(success => {
                if (success) {
                  setLastSyncTime(Date.now());
                  console.log('🎮 Game Data - Created initial Supabase record');
                }
              })
              .catch(error => {
                console.error('🎮 Game Data - Error creating initial record:', error);
                setSyncStatus('error');
              });
          }
        }
      }
      
      // Mark as initialized for this user
      hasInitializedRef.current = true;
      lastUserEmailRef.current = userEmail;
      
    } catch (error) {
      console.error('🎮 Game Data - Error in loadGameData:', error);
      setSyncStatus('error');
      
      // Fallback to localStorage
      const localData = loadFromLocalStorage();
      if (localData) {
        setGameData(localData);
        if (localData.lastSyncTime) {
          setLastSyncTime(localData.lastSyncTime);
        }
      }
    } finally {
      setIsLoading(false);
      isSyncingRef.current = false;
    }
  }, [userData?.email, userData, loadFromLocalStorage, saveToLocalStorage, hasGameProgress]);

  /**
   * Force manual synchronization - reset initialization
   */
  const forceSyncData = useCallback(async (
    setGameData: (data: GameData) => void,
    setLastSyncTime: (time: number) => void,
    setSyncStatus: (status: SyncStatus) => void,
    setIsLoading: (loading: boolean) => void
  ) => {
    console.log('🎮 Game Data - Force sync requested, resetting initialization');
    hasInitializedRef.current = false;
    lastUserEmailRef.current = null;
    await loadGameData(setGameData, setLastSyncTime, setSyncStatus, setIsLoading);
  }, [loadGameData]);

  return {
    loadGameData,
    forceSyncData,
    hasInitializedRef,
    lastUserEmailRef,
    isSyncingRef
  };
};
